class GigiMobileFacetsDropdown extends HTMLElement {
  static ACCORDION_DURATION_MS = 220;

  constructor() {
    super();
    this.isOpen = false;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onPanelClick = this.onPanelClick.bind(this);
    this.onAccordionClick = this.onAccordionClick.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('.gigi-mobile-facets__trigger');
    this.panel = this.querySelector('.gigi-mobile-facets__panel');

    this.trigger?.addEventListener('click', this.onTriggerClick);
    this.panel?.addEventListener('click', this.onPanelClick);
    this.addEventListener('click', this.onAccordionClick);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.isOpen ? this.close() : this.open();
  }

  onAccordionClick(event) {
    const summary = event.target.closest('.gigi-mobile-facets__accordion-summary');
    if (!summary || !this.contains(summary)) return;

    const details = summary.closest('details.gigi-mobile-facets__accordion');
    if (!details) return;

    event.preventDefault();
    this.toggleAccordion(details);
  }

  onPanelClick(event) {
    const sortOption = event.target.closest('.gigi-mobile-facets__sort-option');
    if (!sortOption) return;

    event.preventDefault();

    const form = sortOption.closest('form');
    const input = form?.querySelector('input[name="sort_by"]');
    const accordion = sortOption.closest('.gigi-mobile-facets__accordion--sort');
    const activeLabel = accordion?.querySelector('[data-sort-active-label]');

    if (!form || !input) return;

    input.value = sortOption.dataset.value;
    const sortLabel = sortOption.dataset.sortLabel || sortOption.textContent.trim();
    if (activeLabel) {
      activeLabel.textContent = sortLabel;
    }

    accordion?.querySelectorAll('.gigi-mobile-facets__sort-option').forEach((button) => {
      button.classList.toggle('is-active', button === sortOption);
    });

    if (
      typeof FacetFiltersForm === 'undefined' ||
      !FacetFiltersForm.applyClientSort(input.value, sortLabel, form)
    ) {
      form.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  onDocumentClick(event) {
    if (!this.isOpen || this.contains(event.target)) return;
    this.close();
  }

  open() {
    if (this.isOpen || this.panel.classList.contains('is-hiding')) return;

    this.isOpen = true;
    this.panel.classList.remove('is-hiding');
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.isOpen || this.panel.classList.contains('is-hiding')) return;

    const finish = () => {
      this.panel.classList.remove('is-hiding');
      this.panel.hidden = true;
      this.isOpen = false;
      this.trigger.setAttribute('aria-expanded', 'false');
    };

    if (this.prefersReducedMotion()) {
      finish();
      return;
    }

    this.panel.classList.add('is-hiding');

    const onTransitionEnd = (event) => {
      if (event.target !== this.panel) return;
      this.panel.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(fallback);
      finish();
    };

    this.panel.addEventListener('transitionend', onTransitionEnd);
    const fallback = window.setTimeout(() => {
      this.panel.removeEventListener('transitionend', onTransitionEnd);
      finish();
    }, 280);
  }

  toggleAccordion(details) {
    if (details.open) {
      this.closeAccordion(details);
      return;
    }

    this.openAccordion(details);
  }

  openAccordion(details) {
    const panel = details.querySelector('.gigi-mobile-facets__accordion-panel');
    const summary = details.querySelector('summary');
    if (!panel) {
      details.setAttribute('open', '');
      summary?.setAttribute('aria-expanded', 'true');
      return;
    }

    if (this.prefersReducedMotion()) {
      details.setAttribute('open', '');
      summary?.setAttribute('aria-expanded', 'true');
      return;
    }

    details.setAttribute('open', '');
    summary?.setAttribute('aria-expanded', 'true');
    panel.style.overflow = 'hidden';
    panel.style.height = '0px';
    panel.style.transition = `height ${GigiMobileFacetsDropdown.ACCORDION_DURATION_MS}ms ease`;

    const targetHeight = panel.scrollHeight;

    requestAnimationFrame(() => {
      panel.style.height = `${targetHeight}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;
      panel.style.height = '';
      panel.style.overflow = '';
      panel.style.transition = '';
      panel.removeEventListener('transitionend', onTransitionEnd);
    };

    panel.addEventListener('transitionend', onTransitionEnd);
  }

  closeAccordion(details) {
    const panel = details.querySelector('.gigi-mobile-facets__accordion-panel');
    const summary = details.querySelector('summary');
    if (!panel) {
      details.removeAttribute('open');
      summary?.setAttribute('aria-expanded', 'false');
      return;
    }

    if (this.prefersReducedMotion()) {
      details.removeAttribute('open');
      summary?.setAttribute('aria-expanded', 'false');
      return;
    }

    panel.style.overflow = 'hidden';
    panel.style.transition = `height ${GigiMobileFacetsDropdown.ACCORDION_DURATION_MS}ms ease`;
    panel.style.height = `${panel.scrollHeight}px`;

    requestAnimationFrame(() => {
      panel.style.height = '0px';
    });

    const onTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;
      details.removeAttribute('open');
      summary?.setAttribute('aria-expanded', 'false');
      panel.style.height = '';
      panel.style.overflow = '';
      panel.style.transition = '';
      panel.removeEventListener('transitionend', onTransitionEnd);
    };

    panel.addEventListener('transitionend', onTransitionEnd);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

customElements.define('gigi-mobile-facets-dropdown', GigiMobileFacetsDropdown);
